import type { HapticsCapability } from "..";
import { findMainCanvas } from "../canvas-capture";
import { instrumentSdkMethod } from "../instrumentation";
import {
  CANCEL_MIRRORED_EVENTS,
  PAN_BLOCKING_EVENTS,
  TAP_INPUT_EVENTS,
  TapHaptics,
} from "../tap-haptics";
import type { ModuleContext } from "./module-context";

// Engines that cancel touchend get no click, so the surface goes in after this much quiet instead.
const CLICK_AFTER_TOUCHEND_GRACE_MS = 400;

// Safari 17.4 shipped the switch control, but its haptic tick only arrived in iOS 18.
const MIN_TAP_HAPTICS_IOS = 18;

let routedTapHaptics: TapHaptics | null = null;
let tapInputRouted = false;
const panBlockingCanvases = new WeakSet<EventTarget>();
let clickInProgress = false;

// Installed at load so it precedes capture listeners an engine adds before JestSDK.init().
if (detectCapability() === "tap") {
  routeTapInput();
}

export class HapticsModule {
  private readonly capability: HapticsCapability;
  private attachPending = false;

  constructor(private ctx: ModuleContext) {
    this.capability = detectCapability();
    if (this.capability === "tap") {
      routeTapInput();
    }
  }

  @instrumentSdkMethod({ name: "haptics.getCapability" })
  getCapability(): HapticsCapability {
    return this.capability;
  }

  @instrumentSdkMethod({ name: "haptics.vibrate" })
  vibrate(pattern: number | number[]): boolean {
    if (typeof document === "undefined" || document.hidden) {
      return false;
    }

    switch (this.capability) {
      case "vibrate":
        try {
          return navigator.vibrate(pattern);
        } catch {
          // Browsers may throw for blocked or malformed requests; haptics are best effort.
          return false;
        }
      case "tap": {
        const silent = [pattern].flat().every((duration) => !(duration > 0));
        if (silent) {
          routedTapHaptics?.disarmCurrentTap();
          return false;
        }
        if (
          routedTapHaptics?.isAttached &&
          findMainCanvas(document) === routedTapHaptics.canvas
        ) {
          return routedTapHaptics.armCurrentTap();
        }
        this.attachAfterCurrentTap();
        return false;
      }
      case "none":
        return false;
    }
  }

  instrumentMethod<T>(opts: { method: string; args: unknown; fn: () => T }): T {
    return this.ctx.instrumentMethod(opts);
  }

  // Touches keep their start target but the click is hit-tested on release, so attach between taps.
  private attachAfterCurrentTap(): void {
    if (this.attachPending) {
      return;
    }
    this.attachPending = true;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const attach = () => {
      clearTimeout(timer);
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("touchstart", onTouchStart, true);
      window.removeEventListener("touchend", onTouchEnd, true);
      window.removeEventListener("touchcancel", onTouchEnd, true);
      this.attachPending = false;
      this.attachTapHaptics();
    };
    const onClick = () => {
      clearTimeout(timer);
      timer = setTimeout(attach, 0);
    };
    const onTouchStart = () => clearTimeout(timer);
    const onTouchEnd = (event: Event) => {
      if ((event as TouchEvent).touches.length === 0) {
        clearTimeout(timer);
        timer = setTimeout(attach, CLICK_AFTER_TOUCHEND_GRACE_MS);
      }
    };
    const options = { capture: true, passive: true };
    window.addEventListener("click", onClick, options);
    if (clickInProgress) {
      // The request came from a click handler, so this click is the end of the tap.
      onClick();
    }
    window.addEventListener("touchstart", onTouchStart, options);
    window.addEventListener("touchend", onTouchEnd, options);
    window.addEventListener("touchcancel", onTouchEnd, options);
  }

  private attachTapHaptics(): void {
    routedTapHaptics?.destroy();
    const canvas = findMainCanvas(document);
    routedTapHaptics = canvas?.parentNode
      ? new TapHaptics(canvas, panBlockingCanvases.has(canvas))
      : null;
  }
}

// The game's window and document capture listeners must see only the canvas copy.
function routeTapInput(): void {
  if (tapInputRouted) {
    return;
  }
  tapInputRouted = true;
  const route = (event: Event) => {
    if (
      routedTapHaptics &&
      (routedTapHaptics.owns(event.target) ||
        routedTapHaptics.isOwnFocusChange(event))
    ) {
      event.stopImmediatePropagation();
      const notCancelled = routedTapHaptics.handleInput(event);
      if (!notCancelled && CANCEL_MIRRORED_EVENTS.includes(event.type)) {
        event.preventDefault();
      }
    }
  };
  for (const type of TAP_INPUT_EVENTS) {
    window.addEventListener(type, route, {
      capture: true,
      passive: !CANCEL_MIRRORED_EVENTS.includes(type),
    });
  }

  const noteClick = () => {
    clickInProgress = true;
    setTimeout(() => {
      clickInProgress = false;
    }, 0);
  };
  window.addEventListener("click", noteClick, { capture: true, passive: true });

  // Read after dispatch, so a game that stops the touch's propagation is still seen cancelling it.
  const notePanBlocking = (event: Event) => {
    const target = event.target;
    if (
      !(target instanceof HTMLCanvasElement) ||
      panBlockingCanvases.has(target)
    ) {
      return;
    }
    setTimeout(() => {
      if (event.defaultPrevented) {
        panBlockingCanvases.add(target);
      }
    }, 0);
  };
  for (const type of PAN_BLOCKING_EVENTS) {
    window.addEventListener(type, notePanBlocking, {
      capture: true,
      passive: true,
    });
  }
}

function detectCapability(): HapticsCapability {
  if (typeof navigator === "undefined" || typeof document === "undefined") {
    return "none";
  }
  if (typeof navigator.vibrate === "function") {
    return "vibrate";
  }
  if (
    !/iPhone/.test(navigator.userAgent) ||
    getIOSMajorVersion(navigator.userAgent) < MIN_TAP_HAPTICS_IOS ||
    !("switch" in HTMLInputElement.prototype) ||
    typeof Touch !== "function" ||
    typeof TouchEvent !== "function"
  ) {
    return "none";
  }
  try {
    // WebKit may reject a Touch whose target is not an element, such as the document itself.
    new Touch({ identifier: 0, target: document.documentElement });
    new TouchEvent("touchstart");
    return "tap";
  } catch {
    return "none";
  }
}

function getIOSMajorVersion(userAgent: string): number {
  const match = /OS (\d+)_/.exec(userAgent);
  return match ? Number(match[1]) : 0;
}
